<template>
  <div class="max-w-5xl w-full mx-auto max-md:px-4">
    <div class="filtering">
      <div class="text-3xl font-medium uppercase text-center sm:text-left">{{ $t('Campaigns') }}</div>
      <div class="flex my-4 gap-x-4 justify-center sm:justify-start">
        <button
          id="filter-raffle"
          class="filter-button px-2 py-4"
          :class="{ active: currentFilterType === 'raffle' }"
          @click="filterCampaigns('raffle')"
        >
          {{ $t('Raffle') }}
        </button>
        <button
          id="filter-cashback"
          class="filter-button px-2 py-4"
          :class="{ active: currentFilterType === 'cashback' }"
          @click="filterCampaigns('cashback')"
        >
          {{ $t('Cashback') }}
        </button>
      </div>
    </div>
    <template v-if="campaignsLoaded">
      <div v-show="activeCampaigns.length > 0" class="my-8">
        <div class="w-full pt-4 text-3xl font-bold uppercase text-center sm:text-left">{{ $t('Active Campaigns') }}</div>
        <div
          class="grid grid-cols-1 sm:grid-cols-2 gap-6 content-start justify-center w-full my-4 bg-white"
        >
          <div
            v-for="campaign in activeCampaigns"
            :key="campaign.id"
            class="active-campaign flex items-center justify-between w-full gap-5 flex-col shadow-xl shadow-indigo-100/50 rounded-xl"
          >
            <img
              :src="campaign.bannerDesktop"
              :alt="`${campaign.name} Banner`"
              class="hidden sm:block rounded-t-xl"
            />
            <img
              :src="campaign.bannerMobile"
              :alt="`${campaign.name} Banner`"
              class="block sm:hidden rounded-t-xl"
            />
            <div class="flex flex-col items-center gap-5 pt-4 text-center">
              <span class="text-xl">{{ campaignRange(campaign) }}</span>
              <span class="text-xl">{{ campaign.name }}</span>
            </div>
            <img v-if="campaign.retailerLogo" :src="campaign.retailerLogo" alt="Retailer Logo" class="retailer-logo">
            <button-component
              :id="`participate-button-${campaign.id}`"
              class="campaignBtn w-fit mb-4"
              text="Participate"
              @click.stop="participate(campaign)"
            />
          </div>
        </div>
      </div>
      <div v-show="endedCampaigns.length > 0">
        <div class="block w-full mx-auto mt-16 mb-8 text-3xl font-bold uppercase text-center sm:text-left">
          {{ $t('Past Campaigns') }}
        </div>
        <div
          class="grid grid-cols-1 sm:grid-cols-3 gap-6 content-start justify-center w-full mx-auto my-4"
        >
          <div
            v-for="campaign in paginatedEndedCampaigns"
            :key="campaign.id"
            class="ended-campaign cursor-pointer flex items-center flex-col shadow-xl shadow-indigo-100/50 rounded-xl overflow-hidden"
            style="aspect-ratio: 16/9;"
            @click="participate(campaign)"
          >
            <img
              :src="campaign.bannerDesktop"
              :alt="`${campaign.name} Banner`"
              class="hidden sm:block w-full h-full object-cover opacity-60 hover:opacity-80 transition-opacity"
            />
            <img
              :src="campaign.bannerMobile"
              :alt="`${campaign.name} Banner`"
              class="block sm:hidden w-full h-full object-cover opacity-60 hover:opacity-80 transition-opacity"
            />
          </div>
        </div>
        <div v-if="totalPages > 1" class="flex justify-center items-center gap-2 mt-6 mb-8">
          <button
            class="pagination-btn"
            :disabled="currentPage === 1"
            @click="goToPage(currentPage - 1)"
          >
            &lsaquo;
          </button>
          <template v-for="page in paginationPages" :key="page">
            <span v-if="page === '...'" class="pagination-ellipsis">&#8230;</span>
            <button
              v-else
              class="pagination-btn"
              :class="{ active: page === currentPage }"
              @click="goToPage(page)"
            >
              {{ page }}
            </button>
          </template>
          <button
            class="pagination-btn"
            :disabled="currentPage === totalPages"
            @click="goToPage(currentPage + 1)"
          >
            &rsaquo;
          </button>
        </div>
      </div>
      <div v-if="readyForVerificationCampaigns.length > 0">
        <span
          class="block w-3/4 mx-auto mt-8 pt-4 text-xl font-bold uppercase text-center sm:text-left border-t border-black"
          >{{ $t('Ready for Verification Campaigns') }}</span
        >
        <div
          class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 content-start justify-center w-3/4 mx-auto my-4 bg-white"
        >
          <div
            v-for="campaign in readyForVerificationCampaigns"
            :key="campaign.id"
            class="qa-campaign flex items-center justify-between w-full gap-5 flex-col shadow-xl shadow-indigo-100/50 rounded-xl"
          >
            <img
              :src="campaign.bannerDesktop"
              :alt="`${campaign.name} Banner`"
              class="hidden sm:block rounded-t-xl"
            />
            <img
              :src="campaign.bannerMobile"
              :alt="`${campaign.name} Banner`"
              class="block sm:hidden rounded-t-xl"
            />
            <div class="flex flex-col items-center gap-5 p-4 text-center">
              <span class="text-xl">{{ campaignRange(campaign) }}</span>
              <span class="text-xl">{{ campaign.name }}</span>
            </div>
            <button-component
              :id="`participate-button-${campaign.id}`"
              class="campaignBtn w-fit mb-4"
              text="Participate"
              @click.stop="participate(campaign)"
            />
          </div>
        </div>
      </div>
    </template>
    <div v-else>
      <div class="flex items-center justify-center gap-5 h-screen text-xl text-gray-800">
        <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-500"></div>
        <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-500"></div>
        <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-500"></div>
      </div>
    </div>
  </div>

  <!-- Navigation loading overlay -->
  <div v-if="navigating" class="fixed inset-0 z-50 flex items-center justify-center bg-white/70">
    <div class="flex gap-4">
      <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-500"></div>
      <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-500"></div>
      <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-500"></div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue'
import { useCampaignStore } from '@/stores'
import { format } from 'date-fns'
import { useRouter } from 'vue-router'

const campaignsLoaded = ref(false)
const filteredCampaigns = ref([])
const activeCampaigns = ref([])
const endedCampaigns = ref([])
const readyForVerificationCampaigns = ref([])
const currentFilterType = ref('All')
const currentPage = ref(1)
const itemsPerPage = 6
const navigating = ref(false)
const router = useRouter()

const totalPages = computed(() => Math.ceil(endedCampaigns.value.length / itemsPerPage))
const paginatedEndedCampaigns = computed(() =>
  endedCampaigns.value.slice((currentPage.value - 1) * itemsPerPage, currentPage.value * itemsPerPage)
)
const paginationPages = computed(() => {
  const total = totalPages.value
  const current = currentPage.value
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const pages = []
  pages.push(1)
  if (current > 3) pages.push('...')
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i)
  if (current < total - 2) pages.push('...')
  pages.push(total)
  return pages
})
const goToPage = page => { currentPage.value = page }

const campaignStore = useCampaignStore()

onMounted(async () => {
  try {
    const res = await campaignStore.fetchCampaigns()
    campaignsLoaded.value = res
    filterCampaigns('All')
  } catch (error) {
    console.error('Error fetching campaigns:', error)
  }
})

const filterCampaigns = type => {
  activeCampaigns.value = []
  endedCampaigns.value = []
  currentPage.value = 1

  if (type === currentFilterType.value) {
    currentFilterType.value = 'All'
    filteredCampaigns.value = campaignStore.campaigns
  } else {
    currentFilterType.value = type
    filteredCampaigns.value = campaignStore.campaigns.filter(c => {
      return c.type === type
    })
  }
  const tz = 'Europe/Bucharest';

  // Get current date/time in Romania
  const currentDate = new Date(new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date()));

  filteredCampaigns.value.forEach(campaign => {
    // Get end of campaign day in Romania
    const endDate = new Date(new Date(campaign.uploadEndDate).toLocaleString('en-US', { timeZone: tz }));
    endDate.setHours(23, 59, 59, 999);

    if (campaign.campaignStatus == 'ready-for-verification') {
      readyForVerificationCampaigns.value.push(campaign)
    } else if (endDate < currentDate) {
      endedCampaigns.value.push(campaign)
    } else {
      activeCampaigns.value.push(campaign)
    }
  })
}

const campaignRange = campaign => {
  const start = formatDate(campaign.startDate)
  const end = formatDate(campaign.endDate)
  return start + ' - ' + end
}

const participate = campaign => {
  navigating.value = true
  campaignStore.setActiveCampaign(campaign)
  router.push({
    name: 'CampaignPage',
    params: { campaign: campaign.domain }
  })
}

const terms = campaign => {
  // Create link and add random query parameter to prevent caching
  const link = `${campaign.tnc}?_=${new Date().getTime()}`
  window.open(link, '_blank')
}

// TODO: Uncomment when retailers page is ready
// const retailers = campaign => {
//   campaignStore.setActiveCampaign(campaign)
//   router.push({ name: 'RetailersPage', params: { campaign: campaign.domain } })
// }

const formatDate = function (date, formatString = 'dd.MM.yyyy') {
  return format(new Date(date), formatString)
}
</script>

<style scoped lang="scss">
.filter-button {
  min-width: 120px;
  cursor: pointer;
  user-select: none;
  text-align: center;
  border: 2px solid #1a4091;
  color: #1a4091;
  border-radius: 0.75rem;
  transition: background-color 0.3s, color 0.3s;
  &:hover {
    background-color: #1a4091;
    color: white;
  }
  &.active {
    background-color: #1a4091;
    color: white;
  }
}

img {
  width: 100%;
}

.retailer-logo {
  width: 55px;
  height: auto;
}

.pagination-btn {
  min-width: 36px;
  height: 36px;
  padding: 0 8px;
  border: 2px solid #1a4091;
  border-radius: 0.5rem;
  color: #1a4091;
  font-weight: 500;
  transition: background-color 0.2s, color 0.2s;
  cursor: pointer;
  &:hover:not(:disabled) {
    background-color: #1a4091;
    color: white;
  }
  &.active {
    background-color: #1a4091;
    color: white;
  }
  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
}

.pagination-ellipsis {
  min-width: 24px;
  text-align: center;
  color: #1a4091;
  font-weight: 500;
}
</style>
